/**
 * @module experience/byod/compare-devices
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/*tslint:disable:max-file-line-count*/
/*tslint:disable:max-line-length*/

import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntil, finalize } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { LoggerService } from '@core/logger/services/logger.service';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import {
  HARDGOODS_CONSTANTS,
  DIGITALDATA_CONSTANT,
} from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { HARDGOODS_MODULE_COMPONENTS } from '@experience/shop/hardgoods/constants/hardgoods.constants';
import { CompareDeviceModel } from '@experience/shop/hardgoods/models/compare-device.model';
import { CompareHardgoodsService } from '@experience/shop/hardgoods/services/compare-hardgoods.service';
import { MIN_SELECTED_NUMBER_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

import { Subject } from 'rxjs';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';

import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { BrowseDeviceModel } from '@experience/shop/hardgoods/models/browse-device.model';
import { CurrencyPipe } from '@angular/common';
import { AemAuthorableData } from '@core/aem/interfaces/legal-text/aem-authorable.interface';

@Component({
  selector: 'tmo-compare-devices',
  templateUrl: './compare-devices.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/page-titles.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
  ],
  providers: [CurrencyPipe],
})
export class CompareDevicesComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  public compareDeviceData: CompareDeviceModel[];
  /**
   * AEM content for Compare Devices Page
   */
  public compareDevicesContent: ConfigData;

  /**
   * variable to display no of devices selected
   */
  public selectDevicesToCompare: string;

  /**
   * selectedDevicesToCompareData is used to store array of objects containing comparison data
   */
  public selectedDevicesToCompareData: CompareDeviceModel[];

  /**
   * specificationLabel stores the product categories used for comparison
   * @type {Set<string>}
   */
  public specificationLabel: Set<string>;

  /**
   * billingType is used to indicate prepaid/postpaid
   */
  public billingType: string;

  /**
   * variable to display the AEM data
   */
  public compareDevicesData: string;
  /**
   * variable to display the AEM data
   */
  public chooseDevicesToViewData: string;
  /**
   * variable to show/hide spinner
   */
  public isLoading: boolean;

  /**
   * variable to show selected devices
   */
  public selectedDeviceLength: number;
  /**
   * Stores value of legal text authorable filtered object
   */
  public legalText: AemAuthorableData[];
  /**
   * Un-subscriber for all subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  constructor(
    private compareHardgoodsService: CompareHardgoodsService,
    private commonUtilsService: CommonUtilService,
    private sharedBrowseService: SharedBrowseService,
    private logger: LoggerService,
    private currencyPipe: CurrencyPipe,
    private objectService: ObjectService,
    public routeSnapshot: ActivatedRoute,
    public pdlService: PdlService
  ) {
    super(pdlService, routeSnapshot);
    this.compareDeviceData = [];
    this.specificationLabel = new Set<string>();
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * ngOnInit to initialize the component data
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'entering ngOnInit() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
    this.loadAEMContent();
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'leaving ngOnInit() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
  }

  /**
   * Return to the compare page
   */
  public returnAndNavigateBack(): void {
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'entering returnAndNavigateBack() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
    this.sharedBrowseService.compareState = HARDGOODS_CONSTANTS.COMPARE_DEVICE;
    this.compareHardgoodsService.navigate(this.compareHardgoodsService.presentState);
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'leaving returnAndNavigateBack() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
  }

  /**
   * on device selection
   * @param index
   * @returns void
   */
  public onSelection(index: number): void {
    this.selectedDeviceLength = 0;
    const minSelectedNumber = MIN_SELECTED_NUMBER_CONSTANTS.MIN_SELECTED_NUMBER;
    const compareData: CompareDeviceModel[] = this.compareDeviceData.filter(
      (deviceInfo: CompareDeviceModel) => deviceInfo.isChecked
    );
    if (compareData.length > minSelectedNumber || !this.compareDeviceData[index].isChecked) {
      this.compareDeviceData[index].isChecked = !this.compareDeviceData[index].isChecked;
      this.selectedDevicesToCompareData = [];
      this.compareDeviceData.forEach((product: CompareDeviceModel) => {
        if (product.isChecked) {
          this.selectedDevicesToCompareData.push(product);
          this.selectedDeviceLength++;
        }
        product.rowLabel.forEach((specificationLabel: string) => {
          if (!this.specificationLabel.has(specificationLabel)) {
            this.specificationLabel.add(specificationLabel);
          }
        });
      });
      this.selectDevicesToCompare = this.commonUtilsService.messageFormatter(
        this.compareDevicesContent.selectDevicesToCompare,
        [
          this.selectedDeviceLength.toString().toLowerCase().concat(' '),
          this.compareHardgoodsService.filter.toLowerCase(),
        ]
      );
    }
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'leaving onSelection() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
  }

  /**
   * ngOnDestroy to clear the variables when component scope destroys
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'entering ngOnDestroy() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.compareHardgoodsService.presentState = null;
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'leaving ngOnDestroy() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
  }

  /**
   * Load AEM content for Compare Devices
   * @returns void
   */
  private loadAEMContent(): void {
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'entering loadAEMContent() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
    this.compareHardgoodsService
      .getCompareDevicesContent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((compareDevicesData: ConfigData) => {
        this.compareDevicesContent = this.objectService.get(compareDevicesData, 'configurableData');
        const authorableData = this.objectService.get(compareDevicesData, 'authorableData');
        this.legalText = authorableData.filter(
          (aemData: AemAuthorableData) =>
            aemData.uiComponent === HARDGOODS_MODULE_COMPONENTS.LEGAL_TEXT
        );
        this.getComparisonDeviceData();
        this.logger.debug(
          HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
          HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
          'loaded compare device data in ' +
            HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT +
            ' compareDevicesData: ' +
            JSON.stringify(compareDevicesData)
        );
        window[DIGITALDATA_CONSTANT.DIGITALDATA][DIGITALDATA_CONSTANT.PAGE][
          DIGITALDATA_CONSTANT.PAGEINFO
        ][DIGITALDATA_CONSTANT.PAGENAME] =
          `${DIGITALDATA_CONSTANT.PRODUCT_COMPARE} : ${this.routeSnapshot.snapshot.queryParams.category}`;
        this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'pageLoad');
      });
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'leaving loadAEMContent() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
  }

  /**
   * get compare devices data
   * @returns void
   */
  private getComparisonDeviceData(): void {
    this.isLoading = true;
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'entering getComparisonDeviceData() in ' +
        HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
    this.compareHardgoodsService
      .getComparisonDeviceData()
      .pipe(
        takeUntil(this.ngUnsubscribe.asObservable()),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((compareData: BrowseDeviceModel[]) => {
        this.compareDeviceData = [];
        const compareDataLength = compareData ? compareData.length : 0;
        for (let i = 0; i < compareDataLength; i++) {
          this.compareDeviceData[i] = new CompareDeviceModel(compareData[i], this.objectService);
        }
        this.selectedDevicesToCompareData = this.compareDeviceData;
        this.compareDeviceData.forEach((product: CompareDeviceModel) => {
          product.rowLabel.forEach((specificationLabel: string) => {
            if (!this.specificationLabel.has(specificationLabel)) {
              this.specificationLabel.add(specificationLabel);
            }
          });
        });
        this.formatAEMContent();
      });
    this.logger.debug(
      HARDGOODS_MODULE_COMPONENTS.HARDGOODS_MODULE_NAME,
      HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT,
      'leaving getComparisonDeviceData() in ' + HARDGOODS_MODULE_COMPONENTS.COMPARE_DEVICE_COMPONENT
    );
  }

  /**
   * format aem content
   * @returns void
   */
  private formatAEMContent(): void {
    this.compareHardgoodsService.filterContentUrl();
    this.chooseDevicesToViewData = this.commonUtilsService.messageFormatter(
      this.compareDevicesContent.chooseDevicesToView,
      [this.compareHardgoodsService.filter.toLowerCase()]
    );
    this.compareDevicesData = this.commonUtilsService.messageFormatter(
      this.compareDevicesContent.compareDevices,
      [this.compareHardgoodsService.filter]
    );
    this.selectDevicesToCompare = this.commonUtilsService.messageFormatter(
      this.compareDevicesContent.selectDevicesToCompare,
      [
        this.compareDeviceData.length.toString().toLowerCase().concat(' '),
        this.compareHardgoodsService.filter.toLowerCase(),
      ]
    );
    this.selectedDevicesToCompareData.forEach((product: CompareDeviceModel) => {
      const listPrice = this.currencyPipe.transform(
        this.objectService.get(product, 'listPrice'),
        'USD',
        'symbol-narrow',
        '1.2-2'
      );
      const serviceThresholdPrice = this.currencyPipe.transform(
        this.objectService.get(product, 'serviceThresholdPrice'),
        'USD',
        'symbol-narrow',
        '1.2-2'
      );
      product.withoutQualifyingPlan = this.commonUtilsService.messageFormatter(
        this.compareDevicesContent.withoutQualifyingPlan,
        [listPrice, serviceThresholdPrice]
      );
    });
  }
}
